import React, { Component, useEffect, useRef, useState } from "react";
import {
  withStyles,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@material-ui/core";
import axios from "axios";
import { URL } from "../../config/config";
import NewFlowCardPreview from "./NewFlowCardPreview";
import PillAutocomplete from "./PillAutoComplete";
import { isValidUrl } from "../../helpers/url";

const pillColors = [
  // 🔵 Blues
  { name: "Sky Blue", hex: "#1E88E5" },
  { name: "Deep Blue", hex: "#1565C0" },
  { name: "Cyan", hex: "#00ACC1" },
  { name: "Light Blue", hex: "#039BE5" },

  // 🟢 Greens
  { name: "Emerald", hex: "#2E7D32" },
  { name: "Leaf Green", hex: "#43A047" },
  { name: "Soft Green", hex: "#66BB6A" },
  { name: "Aqua Green", hex: "#26A69A" },

  // 🟡 Yellows / Oranges
  { name: "Bright Yellow", hex: "#FDD835" },
  { name: "Amber", hex: "#FFB300" },
  { name: "Orange", hex: "#FB8C00" },
  { name: "Deep Orange", hex: "#F4511E" },

  // 🔴 Reds / Pinks
  { name: "Crimson", hex: "#E53935" },
  { name: "Fire Red", hex: "#D32F2F" },
  { name: "Soft Pink", hex: "#F06292" },
  { name: "Dark Pink", hex: "#C2185B" },
  { name: "Berry", hex: "#AD1457" },

  // 🟣 Purples
  { name: "Purple", hex: "#8E24AA" },
  { name: "Violet", hex: "#9C27B0" },
  { name: "Deep Violet", hex: "#6A1B9A" },
  { name: "Orchid", hex: "#7B1FA2" },
  { name: "Amethyst", hex: "#AB47BC" },

  // ⚪️ Neutrals / Grays
  { name: "Blue Gray", hex: "#455A64" },
  { name: "Slate", hex: "#546E7A" },
  { name: "Charcoal", hex: "#37474F" },
  { name: "Black", hex: "#212121" },

  // 🟤 Browns
  { name: "Cocoa", hex: "#6D4C41" },
  { name: "Warm Brown", hex: "#8D6E63" },

  // 🌈 Extra Accents
  { name: "Coral", hex: "#FF7043" },
  { name: "Living Coral", hex: "#FF6F61" },
  { name: "Teal", hex: "#009688" },
  { name: "Turquoise", hex: "#26C6DA" },
];

class CreateFlowLibraryForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      actionType: "None",
      flowName: "",
      subtitle: "",
      flowBannerText: "",
      flowTags: "",
      impactPercentage: 0,
      bannerColor: "#8E24AA",
      videoLink: "",
      outcomeHighlights: [
        { icon: "", description: "" },
        { icon: "", description: "" },
        { icon: "", description: "" },
      ],
      isUpdating: false,
      err: "",
      success: false,
      options: props.flowLibraryCategories,
      categories: [],
    };
  }

  componentDidMount = () => {
    const { flow } = this.props;

    if (flow) {
      this.setState({
        flowName: flow.flowName,
        flowBannerText: flow.bannerText,
        flowTags: flow.tags.join(","),
        flowDescription: flow.flowDescription || "",
        subtitle: flow.subtitle || "",
        bannerColor: flow.bannerColor || this.state.bannerColor,
        videoLink: flow.videoLink || this.state.videoLink,
        impactPercentage: flow.impactPercentage || this.state.impactPercentage,
        outcomeHighlights:
          flow.outcomeHighlights.length === 3
            ? flow.outcomeHighlights
            : this.state.outcomeHighlights,
        categories: flow.categories || this.state.categories,
      });
    }
  };

  validateFlow = () => {
    if (!this.state.flowName) {
      return { valid: false, msg: "Flow Name is required" };
    }
    if (!this.state.subtitle) {
      return { valid: false, msg: "Flow Subtitle is required" };
    }
    if (!this.state.impactPercentage) {
      return { valid: false, msg: "Impact Percentage is required" };
    }

    if (
      !Array.isArray(this.state.categories) ||
      this.state.categories.length === 0
    ) {
      return { valid: false, msg: "Categories field is required" };
    }

    if (!this.state.bannerColor) {
      return { valid: false, msg: "Banner Color is required" };
    }

    if (!this.state.videoLink) {
      return { valid: false, msg: "Video Link is required" };
    }

    if (!isValidUrl(this.state.videoLink)) {
      return { valid: false, msg: "Invalid Video Link Url" };
    }

    for (let highlight of this.state.outcomeHighlights) {
      if (!highlight.icon || !highlight.description) {
        return {
          valid: false,
          msg: "All fields in outcome highlights are mandatory",
        };
      }
    }

    if (!this.state.flowDescription) {
      return { valid: false, msg: "Flow Description is required" };
    }
    if (!this.state.flowTags) {
      return { valid: false, msg: "Invalid Flow tags" };
    }

    return { valid: true, msg: "All fields are validated" };
  };

  onSubmit = () => {
    const result = this.validateFlow();

    if (!result.valid) {
      this.setState({ success: false, err: result.msg });
      return;
    } else {
      const t = this.state;
      this.setState({ err: "", isUpdating: true, success: false });
      const flowObject = this.getFlow();
      const flowId = flowObject._id;
      const assistantId = flowObject.assistantId;

      let url = URL + "/superadmin/update-flow-library";

      axios
        .post(url, { flowId, assistantId, flowObject })
        .then((response) => {
          this.setState({
            err: "",
            isUpdating: false,
            success: true,
          });
        })
        .catch((error) => {
          console.log(error?.response?.data?.errorMessage);
          this.setState({
            err:
              error?.response?.data?.errorMessage ||
              "Oops! Something went wrong.",
            isUpdating: false,
            success: false,
          });
        });
    }
  };

  handleInput = (e) => {
    this.setState({
      [e.target.name]: e.target.value,
    });
  };

  getFlow = () => {
    const { flow } = this.props;

    return {
      ...flow,
      flowName: this.state.flowName,
      bannerText: this.state.flowBannerText,
      tags:
        this.state.flowTags
          ?.split(",")
          .filter((tag) => !!tag)
          .map((tag) => tag.trim()) || [],
      flowDescription: this.state.flowDescription,
      subtitle: this.state.subtitle,
      impactPercentage: this.state.impactPercentage,
      bannerColor: this.state.bannerColor,
      videoLink: this.state.videoLink,
      outcomeHighlights: this.state.outcomeHighlights,
      categories: this.state.categories,
    };
  };

  onOutcomeHighlightChange = (e, idx) => {
    const value = e.target.value;
    const key = e.target.name === "highlightIcon" ? "icon" : "description";

    this.setState((prevState) => {
      const newOutcomeHighlights = [...prevState.outcomeHighlights];
      newOutcomeHighlights[idx][key] = value;

      return {
        ...prevState,
        outcomeHighlights: newOutcomeHighlights,
      };
    });
  };

  render() {
    const { classes, inDialog } = this.props;
    return (
      <Box
        className={classes.root}
        py={{ xs: 2, md: inDialog ? 0 : 8 }}
        px={{ xs: 2, md: inDialog ? 2 : 6 }}
      >
        {/* Other fields and preview */}
        <Box>
          <Grid container spacing={6} justify="space-between">
            <Grid item xs={12} md={6} container>
              {/* Flow Name  */}
              <Box my={2} width="100%">
                <Grid item xs={12}>
                  <Typography variant="h4" gutterBottom>
                    Flow name
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="flowName"
                    className={classes.textArea}
                    placeholder="Enter Flow Name here..."
                    onChange={this.handleInput}
                    value={this.state.flowName}
                    disabled={false}
                  />
                </Grid>
              </Box>

              {/* Flow Subtitle  */}
              <Box my={2} width="100%">
                <Grid item xs={12}>
                  <Typography variant="h4" gutterBottom>
                    Subtitle
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="subtitle"
                    className={classes.textArea}
                    placeholder="Enter subtitle here..."
                    onChange={this.handleInput}
                    value={this.state.subtitle}
                    disabled={false}
                  />
                </Grid>
              </Box>

              {/* Impact Percentage */}
              <Box my={2} width="100%">
                <Grid item xs={12}>
                  <Typography variant="h4" gutterBottom>
                    Impact Percentage
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Potential Percentage Impact by using flow
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    type="number"
                    name="impactPercentage"
                    className={classes.textArea}
                    placeholder="Enter Percentage Impact here..."
                    disabled={false}
                    value={this.state.impactPercentage}
                    onChange={(e) =>
                      this.setState({
                        impactPercentage: Number(e.target.value),
                      })
                    }
                  />
                </Grid>
              </Box>

              {/* Tags */}
              <Box my={2} width="100%">
                <Grid item xs={12}>
                  <Typography variant="h4" gutterBottom>
                    Flow Tags
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Your flow tags separated by commas
                    <br /> e.g. - ecommerce,abandoned_cart
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    multiline
                    rows={1}
                    rowsMax={20}
                    name="flowTags"
                    className={classes.textArea}
                    placeholder="Enter your tags in here..."
                    disabled={false}
                    value={this.state.flowTags}
                    onChange={this.handleInput}
                  />
                </Grid>
              </Box>

              {/* Flow Categories*/}
              <Box my={2} width="100%">
                <PillAutocomplete
                  options={this.state.options}
                  onChange={(val) => this.setState({ categories: val })}
                  categories={this.state.categories}
                />
              </Box>

              {/* Banner*/}
              <Box my={2} width="100%">
                <Grid item xs={12}>
                  <Typography variant="h4" gutterBottom>
                    Flow Banner Text
                    <span className={classes.optional}>(Optional)</span>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Your text which will appear as green banner to highlight a
                    special feature assosicated with this flow
                    <br /> e.g. - High ROI
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="flowBannerText"
                    className={classes.textArea}
                    placeholder="Enter features here"
                    onChange={this.handleInput}
                    inputProps={{ maxLength: 60 }}
                    value={this.state.flowBannerText}
                    disabled={inDialog}
                  />
                </Grid>
                <Box style={{ marginTop: "10px" }}>
                  <ColorSwatchPicker
                    value={this.state.bannerColor}
                    onChange={(newBannerHex) =>
                      this.setState({ bannerColor: newBannerHex })
                    }
                  />
                </Box>
              </Box>

              {/* Video Link*/}
              <Box my={2} width="100%">
                <Grid item xs={12}>
                  <Typography variant="h4" gutterBottom>
                    Video Link
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="videoLink"
                    className={classes.textArea}
                    placeholder="Enter video Link here"
                    onChange={this.handleInput}
                    value={this.state.videoLink}
                    disabled={inDialog}
                  />
                </Grid>
              </Box>

              {/* Outcome Higlights*/}
              <Box my={2} width="100%">
                <Grid item xs={12}>
                  <Typography variant="h4" gutterBottom>
                    Outcome highlights
                  </Typography>
                </Grid>
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {Array.from({ length: 3 }).map((_, idx) => {
                    return (
                      <Box
                        style={{ display: "flex", gap: "10px", width: "100%" }}
                      >
                        <Box style={{ display: "flex", flexBasis: "60%" }}>
                          <TextField
                            name="highlightText"
                            className={classes.textArea}
                            placeholder="Enter Description"
                            onChange={(e) =>
                              this.onOutcomeHighlightChange(e, idx)
                            }
                            inputProps={{ maxLength: 60 }}
                            value={
                              this.state.outcomeHighlights[idx].description
                            }
                            disabled={inDialog}
                          />
                        </Box>
                        <Box style={{ display: "flex", flexGrow: 1 }}>
                          <TextField
                            name="highlightIcon"
                            className={classes.textArea}
                            placeholder="Enter Icon here"
                            onChange={(e) =>
                              this.onOutcomeHighlightChange(e, idx)
                            }
                            inputProps={{ maxLength: 60 }}
                            value={this.state.outcomeHighlights[idx].icon}
                            disabled={inDialog}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Description*/}
              <Box my={2} width="100%">
                <Grid item xs={12}>
                  <Typography variant="h4" gutterBottom>
                    Flow Description Text
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="flowDescription"
                    className={classes.textArea}
                    placeholder="Enter flow description here"
                    onChange={this.handleInput}
                    value={this.state.flowDescription}
                    disabled={inDialog}
                  />
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                className={classes.preview}
                style={{ display: window.innerWidth < 767 && "inline-block" }}
              >
                <div
                  style={{
                    minWidth: window.innerWidth >= 767 && "320px",
                    marginBottom: window.innerWidth < 767 && "20px",
                    border: "1px solid #f5f5f5",
                    height: "320px",
                    borderRadius: "5px",
                  }}
                >
                  <NewFlowCardPreview flow={this.getFlow()} />
                </div>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box my={4} />
        {this.state.err ? (
          <Box>
            <Typography
              variant="body2"
              color="error"
              style={{ marginBottom: 12 }}
            >
              {this.state.err}
            </Typography>
          </Box>
        ) : (
          ""
        )}
        {this.state.success ? (
          <Box>
            <Typography variant="h3" color="primary">
              Flow successfully Updated
            </Typography>
          </Box>
        ) : (
          ""
        )}
        <Box my={4}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              {this.state.isUpdating ? (
                <CircularProgress />
              ) : (
                <>
                  {!(inDialog && this.state.success) && (
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={this.onSubmit}
                      style={{ marginRight: 8 }}
                    >
                      Update
                    </Button>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }
}

function ColorSwatchPicker({
  value,
  onChange,
  colors = pillColors,
  circleSize = 28,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const triggerStyle = {
    width: circleSize,
    height: circleSize,
    borderRadius: "50%",
    backgroundColor: value,
    border: "2px solid rgba(0,0,0,0.15)",
    boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
    cursor: "pointer",
    display: "inline-block",
  };

  const popoverStyle = {
    position: "absolute",
    top: circleSize + 8,
    left: 0,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.12)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    borderRadius: 12,
    padding: 12,
    zIndex: 1000,
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(8, 28px)",
    gap: 8,
    maxWidth: 8 * 28 + 7 * 8, // keep popover compact
  };

  const swatchStyle = (hex, selected) => ({
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: hex,
    cursor: "pointer",
    border: selected ? "2px solid #000" : "2px solid rgba(0,0,0,0.08)",
    outline: "none",
    boxShadow: selected ? "0 0 0 3px rgba(0,0,0,0.08)" : "none",
  });

  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      ref={rootRef}
    >
      <div
        role="button"
        aria-label="Choose color"
        title="Choose color"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen((o) => !o);
        }}
        style={triggerStyle}
      />
      {open && (
        <div style={popoverStyle}>
          <div style={gridStyle}>
            {colors.map(({ name, hex }) => (
              <div
                key={name}
                title={name}
                tabIndex={0}
                onClick={() => {
                  onChange(hex);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onChange(hex);
                    setOpen(false);
                  }
                }}
                style={swatchStyle(hex, value === hex)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = (theme) => ({
  root: {
    background: "white",
    borderRadius: "8px",
    minHeight: "200px",
    position: "relative",
  },
  textField: {
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  textArea: {
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  previewWrapper: {
    width: 350,
    [theme.breakpoints.down("sm")]: {
      width: 300,
    },
  },
  previewContainer: {
    minHeight: 160,
    background: "#EBF5F3",
    width: "100%",

    borderRadius: 8,
  },
  preview: {
    display: "inline-grid",
    width: "100%",
    justifyContent: "center",
    position: "sticky",
    top: "100px",
    [theme.breakpoints.down("sm")]: {
      position: "static",
    },
  },
  optional: {
    fontSize: "14px",
    color: "#808080",
    fontWeight: "500",
  },
  inlineCharacterCounter: {
    marginLeft: "auto",
    height: 0,
    bottom: "25px",
    position: "absolute",
    right: "10px",
  },
  characterCounter: {
    height: 0,
    position: "absolute",
    right: 0,
    bottom: "-5px",
  },
});

export default withStyles(styles)(CreateFlowLibraryForm);
